import React, { useState, useEffect } from 'react';
import {
  ClipboardDocumentListIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  UserIcon,
  BuildingOfficeIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  EyeIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiService from '../services/apiService';
import { Assignment } from '../types/index';
import PageHeader from './ui/PageHeader';
import Badge, { BadgeTone } from './ui/Badge';
import Button from './ui/Button';
import Modal from './ui/Modal';
import EmptyState from './ui/EmptyState';

const ASSIGNMENT_STATUS_TONE: Record<string, BadgeTone> = {
  completed: 'success',
  in_progress: 'info',
  accepted: 'warning',
  assigned: 'neutral',
  rejected: 'danger',
  cancelled: 'neutral',
};

const Assignments: React.FC = () => {
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedPriority, setSelectedPriority] = useState<string>('all');
  const [selectedDepartment, setSelectedDepartment] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [page, setPage] = useState(1);
  const limit = 20;

  const queryClient = useQueryClient();

  // Fetch departments
  const { data: departmentsData } = useQuery({
    queryKey: ['departments'],
    queryFn: async () => {
      const response = await apiService.get('/departments');
      return response.data || [];
    }
  });

  // Fetch technicians
  const { data: techniciansData } = useQuery({
    queryKey: ['technicians'],
    queryFn: async () => {
      const response = await apiService.get('/users?role=municipal_staff');
      return response.data || [];
    }
  });

  // Fetch assignments
  const { data: assignmentsData, isLoading, error, refetch } = useQuery({
    queryKey: ['assignments', selectedStatus, selectedPriority, selectedDepartment, page, searchTerm],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(selectedStatus !== 'all' && { status: selectedStatus }),
        ...(selectedPriority !== 'all' && { priority: selectedPriority }),
        ...(selectedDepartment !== 'all' && { department_id: selectedDepartment }),
      });
      const response = await apiService.get(`/assignments?${params}`);
      // apiService.get returns response.data, which should be { success: true, data: [...], pagination: {...} }
      return response.success ? response : { success: false, data: [], pagination: { total: 0 } };
    }
  });

  const assignments = (assignmentsData?.data || []) as Assignment[];
  const totalAssignments = assignmentsData?.pagination?.total || 0;
  const totalPages = Math.ceil(totalAssignments / limit);

  // Update assignment status mutation
  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status, notes }: { id: string; status: string; notes?: string }) => {
      const response = await apiService.patch(`/assignments/${id}/status`, { status, notes });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assignments'] });
      setShowEditModal(false);
      setSelectedAssignment(null);
    }
  });

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      refetch();
    }, 30000);
    return () => clearInterval(interval);
  }, [refetch]);

  const filteredAssignments = assignments.filter((assignment: Assignment) => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        assignment.reportTitle?.toLowerCase().includes(searchLower) ||
        assignment.reportNumber?.toLowerCase().includes(searchLower) ||
        assignment.assignedToName?.toLowerCase().includes(searchLower) ||
        assignment.departmentName?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  const handleViewDetails = (assignment: Assignment) => {
    setSelectedAssignment(assignment);
    setShowDetailsModal(true);
  };

  const handleEdit = (assignment: Assignment) => {
    setSelectedAssignment(assignment);
    setShowEditModal(true);
  };

  const handleStatusUpdate = (newStatus: string, notes?: string) => {
    if (selectedAssignment) {
      updateStatusMutation.mutate({
        id: selectedAssignment.id,
        status: newStatus,
        notes
      });
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div>
      <PageHeader
        title="Assignments"
        subtitle="View and manage all technician assignments across departments"
      />

      {/* Filters and Search */}
      <div className="mb-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <FunnelIcon className="h-4 w-4 inline mr-2" />
              Status
            </label>
            <select
              value={selectedStatus}
              onChange={(e) => {
                setSelectedStatus(e.target.value);
                setPage(1);
              }}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Statuses</option>
              <option value="assigned">Assigned</option>
              <option value="accepted">Accepted</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="rejected">Rejected</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* Priority Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <ExclamationTriangleIcon className="h-4 w-4 inline mr-2" />
              Priority
            </label>
            <select
              value={selectedPriority}
              onChange={(e) => {
                setSelectedPriority(e.target.value);
                setPage(1);
              }}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Priorities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>

          {/* Department Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <BuildingOfficeIcon className="h-4 w-4 inline mr-2" />
              Department
            </label>
            <select
              value={selectedDepartment}
              onChange={(e) => {
                setSelectedDepartment(e.target.value);
                setPage(1);
              }}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Departments</option>
              {(departmentsData || []).map((dept: any) => (
                <option key={dept.id} value={dept.id}>
                  {dept.name}
                </option>
              ))}
            </select>
          </div>

          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <MagnifyingGlassIcon className="h-4 w-4 inline mr-2" />
              Search
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by report, technician, department..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Refresh Button */}
        <div className="flex justify-end">
          <Button variant="primary" icon={ArrowPathIcon} onClick={() => refetch()}>
            Refresh
          </Button>
        </div>
      </div>

      {/* Assignments Table */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {isLoading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading assignments...</p>
          </div>
        ) : error ? (
          <div className="p-12 text-center text-red-600">
            <p>Error loading assignments. Please try again.</p>
          </div>
        ) : filteredAssignments.length === 0 ? (
          <div className="p-12 text-center text-gray-500 dark:text-gray-400">
            <ClipboardDocumentListIcon className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <p>No assignments found matching your filters.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Report
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Assigned To
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Department
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Priority
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Dates
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredAssignments.map((assignment: Assignment) => (
                    <tr key={assignment.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {assignment.reportTitle || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {assignment.reportNumber || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <UserIcon className="h-5 w-5 text-gray-400 mr-2" />
                          <div className="text-sm text-gray-900 dark:text-gray-100">
                            {assignment.assignedToName || 'Unassigned'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-gray-100">
                          {assignment.departmentName || 'N/A'}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <Badge tone={ASSIGNMENT_STATUS_TONE[assignment.status] || 'neutral'} value={assignment.status} />
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <Badge value={assignment.priority || 'medium'} />
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                        <div>Est: {formatDate(assignment.estimatedCompletionDate)}</div>
                        {assignment.actualCompletionDate && <div>Actual: {formatDate(assignment.actualCompletionDate)}</div>}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" title="View Details" icon={EyeIcon} onClick={() => handleViewDetails(assignment)} />
                          <Button size="sm" variant="ghost" title="Edit Status" icon={PencilIcon} onClick={() => handleEdit(assignment)} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  Showing {(page - 1) * limit + 1} to {Math.min(page * limit, totalAssignments)} of {totalAssignments} assignments
                </div>
                <div className="flex gap-2">
                  <Button variant="secondary" size="sm" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                    Previous
                  </Button>
                  <Button variant="secondary" size="sm" disabled={page === totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Details Modal */}
      <Modal
        open={showDetailsModal && !!selectedAssignment}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedAssignment(null);
        }}
        title="Assignment Details"
        size="lg"
        footer={
          <Button
            variant="secondary"
            onClick={() => {
              setShowDetailsModal(false);
              setSelectedAssignment(null);
            }}
          >
            Close
          </Button>
        }
      >
        {selectedAssignment && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Report</label>
                <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedAssignment.reportTitle}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">#{selectedAssignment.reportNumber}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
                  <div className="mt-1">
                    <Badge tone={ASSIGNMENT_STATUS_TONE[selectedAssignment.status] || 'neutral'} value={selectedAssignment.status} />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Priority</label>
                  <div className="mt-1">
                    <Badge value={selectedAssignment.priority || 'medium'} />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Assigned To</label>
                <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedAssignment.assignedToName || 'Unassigned'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Department</label>
                <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedAssignment.departmentName || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Assigned By</label>
                <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedAssignment.assignedByName || 'System'}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Estimated Completion</label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{formatDate(selectedAssignment.estimatedCompletionDate)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Actual Completion</label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{formatDate(selectedAssignment.actualCompletionDate)}</p>
                </div>
              </div>
              {selectedAssignment.notes && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Notes</label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">{selectedAssignment.notes}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Created</label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{formatDate(selectedAssignment.createdAt)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Last Updated</label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{formatDate(selectedAssignment.updatedAt)}</p>
                </div>
              </div>
            </div>
        )}
      </Modal>

      {/* Edit Status Modal */}
      <Modal
        open={showEditModal && !!selectedAssignment}
        onClose={() => {
          setShowEditModal(false);
          setSelectedAssignment(null);
        }}
        title="Update Assignment Status"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setShowEditModal(false);
                setSelectedAssignment(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={updateStatusMutation.isPending}
              onClick={() => {
                const newStatus = (document.getElementById('newStatus') as HTMLSelectElement)?.value;
                const notes = (document.getElementById('notes') as HTMLTextAreaElement)?.value;
                if (newStatus) {
                  handleStatusUpdate(newStatus, notes || undefined);
                }
              }}
            >
              {updateStatusMutation.isPending ? 'Updating…' : 'Update Status'}
            </Button>
          </>
        }
      >
        {selectedAssignment && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Report</label>
                <p className="text-sm text-gray-900 dark:text-gray-100">{selectedAssignment.reportTitle}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Current Status</label>
                <Badge tone={ASSIGNMENT_STATUS_TONE[selectedAssignment.status] || 'neutral'} value={selectedAssignment.status} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">New Status</label>
                <select
                  id="newStatus"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  defaultValue={selectedAssignment.status}
                >
                  <option value="assigned">Assigned</option>
                  <option value="accepted">Accepted</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="rejected">Rejected</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Notes (Optional)</label>
                <textarea
                  id="notes"
                  rows={4}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Add any notes about this status change..."
                />
              </div>
            </div>
        )}
      </Modal>
    </div>
  );
};

export default Assignments;

